import {
  Component,
  OnInit,
  ChangeDetectorRef,
  NgZone,
  HostBinding,
  HostListener,
  OnDestroy,
  Input,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  inject,
  effect,
} from '@angular/core';
import { RecorderStore } from './recorder/state/recorder.store';
import { STARTER_VIBES, VIBE_GROUPS } from './recorder/vibes/vibes';
import type { Vibe, VibeGroup, Particle } from './recorder/vibes/vibes';
import { VIDEO_EFFECTS } from './recorder/vibes/effects';
import type { VideoEffect } from './recorder/vibes/effects';
import { ThemeService, liftToLight } from './recorder/services/theme.service';
import type { ThumbItem } from './recorder/state/recorder.types';
import { initiateRecorder } from '../../assets/build-recorder/super-start26829.js';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Data,
  SongService,
  ProfileService,
  SingService,
  RecordingService,
  UtilService,
  UserService,
} from '@app/core';
import { MessageService } from '../core/services/message.service';
import { ToastrService } from 'ngx-toastr';
// import { NgxGlideComponent } from 'ngx-glide';
import { FrameSliderComponent, Frame } from '../shared/frame-slider/frame-slider.component';
import Bugsnag from '@bugsnag/js';
import { environment } from './../../environments/environment';
import { now } from 'lodash';
import { Cookie } from 'ng2-cookies/ng2-cookies';
declare var gtag;
// liftToLight moved to theme.service.ts — imported below as part of the shared
// theme helpers so the brightness slider gradient and the fill light overlay
// can use the same lit endpoint logic.

@Component({
  selector: 'app-superrecorder',
  templateUrl: './superrecorder.component.html',
  styleUrls: ['./superrecorder.component.scss'],
})
export class SuperrecorderComponent implements OnInit, OnDestroy {
  @HostListener('window:click', ['$event'])
  clickout(event) {
    if (event.target.tagName != 'svg' && event.target.tagName != 'use') {
      if (this.micDropDown) {
        this.micDropDown = false;
      }
      if (this.videoDropDown) {
        this.videoDropDown = false;
      }
    }
    const target = event.target as HTMLElement;
    const inRightPanel = !!target.closest('.shell__panel--right');
    // Skip auto-closing Themes while the theme builder is open — outside
    // clicks happen often when dismissing the native color picker (e.g.,
    // clicking the video stage) and they'd otherwise drop the user out of
    // the form. Save Theme / Create toggle are the explicit exit paths.
    if (this.themesExpanded && !this.themeBuilderActive && !inRightPanel && !target.closest('[data-vis-grid="theme"]')) {
      this.commitThemes();
    }
    if (this.filtersExpanded && !inRightPanel && !target.closest('[data-vis-grid="filter"]')) {
      this.commitFilters();
    }
    if (this.framesExpanded && !inRightPanel && !target.closest('[data-vis-grid="frame"]')) {
      this.commitFrames();
    }
  }
  @HostListener('window:keydown', ['$event'])
  keyEvent(e: KeyboardEvent) {
    // Don't hijack keys (especially Space → play/pause) while the user is typing
    // in a form field — e.g. the report note, the sync input, or the editable
    // value pills. Otherwise Space toggles playback and never reaches the field.
    const t = e.target as HTMLElement;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) {
      return;
    }
    if (e.keyCode == 32) {
      if ((this.loaded = true && !this.uploaded)) {
        switch (this.UIState) {
          case 'recording':
            this.stopRecording();
            break;
          case 'practicing':
            this.pause();
            break;
          case 'practicingPaused':
            this.play();
            break;
          case 'review':
            this.pause();
            break;
          case 'reviewPaused':
            this.play();
            break;
          case 'default':
            this.play();
            break;
        }
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    } else if (e.code == 'ControlLeft' || e.code == 'ControlRight') {
      e.stopImmediatePropagation();
      this.isControlPressed = true;
      e.preventDefault();
    } else {
      this.isControlPressed = false;
    }
  }
  @ViewChild('recVideo') private recVideo!: ElementRef;
  @ViewChild('duetVideo') private duetVideo!: ElementRef;
  @ViewChild('themesToggle') private themesToggle!: ElementRef<HTMLButtonElement>;
  @ViewChild('filtersToggle') private filtersToggle!: ElementRef<HTMLButtonElement>;
  @ViewChild('framesToggle') private framesToggle!: ElementRef<HTMLButtonElement>;
  @ViewChild('vibesToggle') private vibesToggle!: ElementRef<HTMLButtonElement>;
  @ViewChild('effectsToggle') private effectsToggle!: ElementRef<HTMLButtonElement>;
  @ViewChild('topUI') private topUI!: ElementRef;
  @ViewChild('bottomUI') private bottomUI!: ElementRef;
  @ViewChild('countdown') private countdown!: ElementRef;
  @ViewChild('errorMessage') private errorMessage!: ElementRef;
  @ViewChild('errorMessageContainer') private errorMessageContainer!: ElementRef;
  //@ViewChild('lyricsCanvas') private canvas: ElementRef;
  @ViewChild('filterForm') private filterForm!: ElementRef;
  @ViewChild('filterUI') private filterUI!: ElementRef;
  @ViewChild('framesUI') private framesUI!: ElementRef;
  @ViewChild('filterCanvas') private filterCanvas!: ElementRef;
  @ViewChild('otherSingers', { static: true }) private otherSingersElem!: any;
  @ViewChild('recorderContainer', { static: true }) private recorderContainer!: any;
  @ViewChild('camTriggerBtn') private camTriggerBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild('micTriggerBtn') private micTriggerBtn!: ElementRef<HTMLButtonElement>;
  // @ViewChild(NgxGlideComponent, { static: false }) ngxGlide: NgxGlideComponent;

  readonly recorderStore = inject(RecorderStore);
  readonly themeService  = inject(ThemeService);
  readonly String = String;

  id;
  wasmPath = '/assets/superpowered.wasm';
  workletPath = '/assets/scripts/workletProcessor.js';

  isControlPressed = false;
  blueLimitReached = false;
  pitchDisabled = false;
  song;
  recorder;
  loaded = false;
  duration = 0;
  title;
  partSelector = false;
  playhead = 0;
  parts;
  interval = null;
  uuid;
  framesPaths: Frame[] = [
    {
      name: 'Fine Art',
      path: 'assets/images/frames/frame1.png',
      promo: false,
    },
    {
      name: 'Dressing Room',
      path: 'assets/images/frames/frame2.png',
      promo: false,
    },
    {
      name: "Film '76",
      path: 'assets/images/frames/frame3.png',
      promo: false,
    },
    {
      name: "Film '84",
      path: 'assets/images/frames/frame4.png',
      promo: false,
    },
    {
      name: 'Warm Roses',
      path: 'assets/images/frames/frame5.png',
      promo: false,
    },
    {
      name: 'Cool Roses',
      path: 'assets/images/frames/frame6.png',
      promo: false,
    },
    {
      name: 'Family Photo',
      path: 'assets/images/frames/frame7.png',
      promo: false,
    },
    {
      name: 'Nebula',
      path: 'assets/images/frames/frame8.png',
      promo: false,
    },
    {
      name: 'Space',
      path: 'assets/images/frames/frame9.png',
      promo: false,
    },
    {
      name: 'Chalkboard',
      path: 'assets/images/frames/frame10.png',
      promo: false,
    },
    {
      name: 'Hearts',
      path: 'assets/images/frames/frame11.png',
      promo: false,
    },
    {
      name: 'Autumn',
      path: 'assets/images/frames/frame12.png',
      promo: false,
    },
    {
      name: 'Winter',
      path: 'assets/images/frames/frame13.png',
      promo: false,
    },
    {
      name: 'Stars',
      path: 'assets/images/frames/frame14.png',
      promo: false,
    },
    {
      name: 'Happy Birthday',
      path: 'assets/images/frames/frame15.png',
      promo: false,
    },
    {
      name: 'Snapiversary',
      path: 'assets/images/frames/frame16.png',
      promo: false,
    },
    {
      name: 'Easter Eggs',
      path: 'assets/images/frames/frame17.png',
      promo: false,
    },
    {
      name: "Saint Patrick's Day",
      path: 'assets/images/frames/frame18.png',
      promo: false,
    },
    {
      name: 'Spring Flowers',
      path: 'assets/images/frames/frame19.png',
      promo: false,
    },
    {
      name: 'Cherry Blossom',
      path: 'assets/images/frames/frame20.png',
      promo: false,
    },
    {
      name: 'Sunset',
      path: 'assets/images/frames/frame21.png',
      promo: false,
    },
    {
      name: 'Lavender',
      path: 'assets/images/frames/frame22.png',
      promo: false,
    },
    {
      name: 'Cowboy',
      path: 'assets/images/frames/frame23.png',
      promo: false,
    },
    {
      name: 'Thunder',
      path: 'assets/images/frames/frame24.png',
      promo: false,
    },
  ];
  UIState = 'default';
  containerOpen = false;
  sources: any = {};
  step = 'Ready to Record';
  filterIndex;
  videoDropDown = false;
  micDropDown = false;
  finished = false;
  scrubberOn = false;
  volumeBar = false;
  filters = false;
  videoFlip = false;
  // ── Vibes: tap-to-fire burst overlays that bake into the recorded video ──
  vibes: Vibe[] = STARTER_VIBES;
  vibeGroups: VibeGroup[] = VIBE_GROUPS; // grouped view for the moveable box
  vibesExpanded = localStorage.getItem('ssVibesExpanded') === 'true'; // Presentation dropdown
  // Video Effects — persistent full-frame overlay selected in the Presentation panel.
  videoEffects: VideoEffect[] = VIDEO_EFFECTS;
  selectedEffectId = localStorage.getItem('ssVideoEffect') ?? 'none';
  effectsExpanded = localStorage.getItem('ssEffectsExpanded') === 'true';
  // Moveable Vibes box (draggable panel of vibe buttons over the recorder).
  vibesBoxOpen = localStorage.getItem('ssVibesBoxOpen') !== 'false'; // default shown
  vibesBoxX = parseFloat(localStorage.getItem('ssVibesBoxX') ?? '4'); // % of #recorder
  vibesBoxY = parseFloat(localStorage.getItem('ssVibesBoxY') ?? '70');
  vibesBoxOpacity = parseFloat(localStorage.getItem('ssVibesBoxOpacity') ?? '1'); // 0.2–1
  vibesBoxVertical = localStorage.getItem('ssVibesBoxVertical') === 'true'; // row vs column
  private boxDragging = false;
  private boxGrabDX = 0;
  private boxGrabDY = 0;
  private activeBursts: { vibe: Vibe; startTime: number; particles: Particle[] }[] = [];
  private vibesPatched = false;
  filterName = 'none';
  filterItems = [
    { nameStart: 'blur(', value: '0', nameEnd: 'px) ', id: 'blur' },
    { nameStart: 'brightness(', value: '1', nameEnd: ') ', id: 'brightness' },
    { nameStart: 'contrast(', value: '100', nameEnd: '%) ', id: 'contrast' },
    { nameStart: 'saturate(', value: '100', nameEnd: '%)', id: 'saturate' },
    {
      nameStart: 'hue-rotate(',
      value: '0',
      nameEnd: 'deg) ',
      id: 'hue-rotate',
    },
    { nameStart: 'grayscale(', value: '0', nameEnd: '%) ', id: 'grayscale' },
    { nameStart: 'sepia(', value: '0', nameEnd: '%) ', id: 'sepia' },
    { nameStart: 'invert(', value: '0', nameEnd: '%) ', id: 'invert' },
  ];
  pitchBar = false;
  syncBar = false;
  showSyncModal = false;
  micCalPhase: 'idle' | 'listening' | 'done' | 'error' = 'idle';
  micCalProgress = 0;
  micCalLevel = 0;
  micCalMessage = '';
  micBoostLabel: string | null = null;
  micBoostOn = false;
  micCalTarget = 0.35;
  lyricBar = false;
  lyricSpeed = 0;
  lyricSizeBar = false;
  lyricSize = 0;
  lyricsVisible = true;
  contrast = 50;
  duet = false;
  uploaded = false;
  cam = true;
  sync = 0;
  mmsb = true;
  pitch = 0;
  micVolume = 1;
  count = 5;
  musicVolume = 1;
  params;
  part;
  jam = false;
  urlChanges = false;
  version = '8.0';
  fullscreen = false;
  fsPanelLeftOpen = false;
  fsPanelRightOpen = false;
  restarting = false;
  filterMenu = false;
  framesMenu = false;
  practiceElapsed = 0;
  practiceTimerInterval: any = null;
  framesLoaded = false;
  currentFrameName = 'None';
  filtersExpanded = false;
  framesExpanded = false;
  themesExpanded = false;
  /** Mirrors ThemePickerComponent.builderOpen via its (builderActive) output —
   *  used to skip the window-click auto-close while the user is editing a theme. */
  themeBuilderActive = false;
  filterFocusIndex = 0;
  frameFocusIndex = 0;
  private filterOriginalIndex: number | null = null;
  private frameOriginalName: string | null = null;
  UI = {
    recordButton: true,
    filterButton: true,
    framesButton: true,
    practiceButton: true,
    reviewButton: false,
    restartButton: false,
    pitchButton: true,
    volumeButton: true,
    lyricButton: false,
    lyricSizeButton: true,
    syncButton: false,
    mmsbButton: false,
    cameraButton: true,
    micButton: true,
    saveOrTryAgainButtons: false,
    pauseButton: false,
    stopRecordingButton: false,
    resumePracticeButton: false,
  };

  activeAudioDeviceId: string | null = null;
  activeVideoDeviceId: string | null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private utilService: UtilService,
    private singService: SingService,
    private songService: SongService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private recordingService: RecordingService,
    private profileService: ProfileService,
    private data: Data,
    private router: Router,
    private userService: UserService,
    private messageService: MessageService,
  ) {
    // Bridge RecorderStore AMG state to the recorder engine.
    effect(() => {
      const state = this.recorderStore.autoMicGain();

      // Turn off: remove boost chain, clear persisted keys, and reset UI state.
      // Always clear localStorage (safe even before recorder is ready — removes
      // non-existent keys harmlessly). Only call removeBoostChain once recorder exists.
      if (state === 'off') {
        localStorage.removeItem('ssMicBoostOn');
        localStorage.removeItem('ssMicBoost');
        if (this.recorder) {
          this.recorder?.recorder?.removeBoostChain?.();
          this.micBoostOn = false;
          this.micBoostLabel = null;
          this.detectChanges();
        }
        return;
      }

      if (state !== 'calculating') return;
      if (!this.recorder?.recorder?.startAutoCalibration) return;

      this.recorder.recorder.startAutoCalibration(
        (progress: number) => {
          this.recorderStore.setAutoMicGainProgress(progress);
        },
        (newBoost: number | null, _detail: any) => {
          // If user disabled AMG while calibration was running, undo any boost
          // the recorder JS already applied and bail out.
          if (this.recorderStore.autoMicGain() !== 'calculating') {
            this.recorder?.recorder?.removeBoostChain?.();
            localStorage.removeItem('ssMicBoostOn');
            localStorage.removeItem('ssMicBoost');
            return;
          }
          const isFirefox = /Firefox/.test(navigator.userAgent);
          const activeMicLabel = (
            this.sources?.audio?.find(
              (s: any) => s.deviceId === this.activeAudioDeviceId
            )?.label ?? ''
          ).toLowerCase();
          const isInternalMic = /built-?in|internal|laptop|macbook|imac/.test(activeMicLabel);
          const gain = newBoost !== null ? newBoost : (isFirefox && isInternalMic ? 40 : 5);
          const dB = 20 * Math.log10(gain);
          this.recorderStore.completeAutoMicGain(dB);
          if (newBoost === null) {
            this.recorder.recorder.applyBoostChain(gain).catch(() => {});
          }
          localStorage.setItem('ssMicBoostOn', 'true');
          localStorage.setItem('ssMicBoost', String(gain));
          setTimeout(() => this.recorderStore.settleAutoMicGain(), 1800);
        },
        this.micCalTarget
      );
    });
  }

  ngOnInit() {
    // Restore persisted UI values
    if (sessionStorage.getItem('ssFullscreenRestore')) {
      this.fullscreen = true;
      sessionStorage.removeItem('ssFullscreenRestore');
    }
    const savedLyricSize = parseInt(localStorage.getItem('ssLyricSize') || '0', 10);
    if (savedLyricSize > 0) {
      this.lyricSize = savedLyricSize;
      document.documentElement.style.setProperty('--lyric-scale', String(1 + savedLyricSize * 0.15));
    }
    const savedContrast = localStorage.getItem('ssContrast');
    if (savedContrast !== null) {
      this.contrast = parseInt(savedContrast, 10);
    }
    this._applyContrast(this.contrast);
    this.videoFlip = localStorage.getItem('ssVideoFlip') === 'true';
    // Restore filters/frames panel expanded state — if the user left
    // the recorder with either grid open, reopen it on next song.
    if (localStorage.getItem('ssFiltersExpanded') === 'true') {
      this.filtersExpanded = true;
    }
    if (localStorage.getItem('ssFramesExpanded') === 'true') {
      this.framesExpanded = true;
    }
    this.singService.getFrames().subscribe((result) => {
      let frames = this.framesPaths;
      result.forEach((frame) => {
        frames.unshift({ name: frame.title, path: frame.uri, promo: true });
      });
      frames.unshift({ name: 'None', path: 'nadda', promo: false });
      this.framesPaths = frames;
      this.framesLoaded = true;
      this.activatedRoute.params.subscribe((params) => {
        this.id = parseInt(params.id);
        this.loadSong();
      });
    });
    this.activatedRoute.data.subscribe((data) => {
      if (typeof data.jam != 'undefined') {
        if (data.jam == true) {
          this.jam = true;
        }
      }
    });

    this.activatedRoute.queryParams.subscribe((params) => {
      this.params = params;
      if (typeof params.pitch != 'undefined') {
        this.pitch = params.pitch;
        this.urlChanges = true;
      }
      if (typeof params.volume != 'undefined') {
        this.musicVolume = params.volume;
        this.urlChanges = true;
      }
      if (typeof params.filterIndex != 'undefined') {
        this.filterIndex = params.filterIndex;
        this.filterName = params.filterName;
        this.urlChanges = true;
      }
    });
    if (!this.profileService.user.gold) {
      this.UI.pitchButton = false;
    }
  }

  ngAfterViewInit(){
  }


  detectChanges() {
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }
  initPartSelector(song) {
    //lets sort out the part colours for the part colour selector
    Object.keys(song.parts).forEach((partName) => {
      if (
        !song.parts[partName] ||
        song.parts[partName] == '' ||
        song.parts[partName] == ' '
      ) {
        delete song.parts[partName];
      }
      if (
        partName.toUpperCase().includes('FE') ||
        partName.toUpperCase().includes('WO') ||
        partName.toUpperCase().includes('GIRL')
      ) {
        song.parts[partName] = 'Orange';
      } else if (
        partName.toUpperCase().includes('MAL') ||
        partName.toUpperCase().includes('BOY') ||
        partName.toUpperCase().includes('MEN') ||
        partName.toUpperCase().includes('GUY')
      ) {
        song.parts[partName] = 'SlateBlue';
      } else if (partName.toUpperCase().includes('SINGER 1')) {
        song.parts[partName] = 'rgb(49, 49, 255)';
      } else if (partName.toUpperCase().includes('SINGER 2')) {
        song.parts[partName] = 'Salmon';
      } else if (partName.toUpperCase().includes('SINGER 3')) {
        song.parts[partName] = 'Red';
      } else if (partName.toUpperCase().includes('SINGER 4')) {
        song.parts[partName] = 'Purple';
      }
    });
    this.parts = song.parts;
    if (Object.keys(song.parts).length > 0) {
      this.partSelector = true;
    }
  }
  async loadSong() {
    if (!this.profileService.user.gold) {
      this.pitchDisabled = true;
    }
    if (!this.jam) {
      this.songService.singASong(this.id).subscribe(
        (result) => {
          this.song = result;
          this.song.jam = this.jam; //recorder needs to know whether or not to load in duet mode
          this.title = this.song.title;
          this.sendGtagLoad(
            this.id,
            this.profileService.user.gold,
            'true',
            'none'
          );

            this.initRecorder();

        },
        (error) => {
          if (error.status == 412) {
            this.blueLimitReached = true;
            this.loaded = true;
            this.UIState = 'noPermissions';
            this.sendGtagLoad(
              this.id,
              this.profileService.user.gold,
              'true',
              'blueLimitReached'
            );
            this.changeUIState();
            let errMsg = error.toString();
            Bugsnag.notify('Recorder loadsong error: ' + errMsg);
          }
        }
      );
    } else {
      //this.UI.volumeButton = false;
      this.recordingService.getDuetRecording(this.id).subscribe(
        (result) => {
          this.song = { song: result['song'], recordingData: result['record'] };
          this.title = this.song.recordingData.title;

          //build duet layers for email
          let loggedInUserId = this.profileService.user.id;
          if (this.song.song.users.length) {
            for (var i = 0; i < this.song.song.users.length; i++) {
              let idFound = this.data.recordingDuetLayers.find((value) => {
                if (value.id === this.song.song.users[i].id) return true;
              });
              if (!idFound) {
                let isMe = false;
                if (this.song.song.users[i].id === loggedInUserId) {
                  isMe = true;
                }
                this.data.recordingDuetLayers.push({
                  id: this.song.song.users[i].id,
                  name: this.song.song.users[i].screen_name,
                  email: this.song.song.users[i].email,
                  newsletter: this.song.song.users[i].newsletter,
                  sender_name: this.profileService.user.screen_name,
                  sender_id: this.profileService.user.id,
                  isMe: isMe,
                  title: this.title,
                });
              }
            }
          }

          this.song.jam = this.jam; //recorder needs to know whether or not to load in duet mode
          this.sendGtagLoad(
            this.id,
            this.profileService.user.gold,
            'false',
            'none'
          );
          this.initRecorder();
        },
        (error) => {
          if (error.status == 503) {
            //this.legacyDuet = true;
            this.sendGtagLoad(
              this.id,
              this.profileService.user.gold,
              'false',
              '503'
            );
            let errMsg = error.toString();
            Bugsnag.notify('Recorder loadduet error: ' + errMsg);
          }
          if (error.status == 412) {
            this.blueLimitReached = true;
            this.sendGtagLoad(
              this.id,
              this.profileService.user.gold,
              'false',
              'blueLimitReached'
            );
            let errMsg = error.toString();
            Bugsnag.notify('Recorder load error: ' + errMsg);
          }
        }
      );
    }
  }
  selectPart(part) {
    this.part = part;
    this.partSelector = false;
  }
  initRecorder() {
    // Only bridge cookie → localStorage if no device already saved
    // Avoids overwriting a valid saved device with a stale cookie value
    if (!localStorage.getItem('audioDevice')) {
      const cookieAudio = Cookie.get('recorder-audio-device-id');
      if (cookieAudio) localStorage.setItem('audioDevice', cookieAudio);
    }
    if (!localStorage.getItem('videoDevice')) {
      const cookieVideo = Cookie.get('recorder-video-device-id');
      if (cookieVideo) localStorage.setItem('videoDevice', cookieVideo);
    }

    this.recorder = initiateRecorder(
      this.workletPath,
      this.wasmPath,
      this.recVideo.nativeElement,
      this.duetVideo.nativeElement,
      this.song,
      false,
      this.filterCanvas.nativeElement,
      this.framesPaths
    );
    if (typeof this.recorder == 'string') {
      if (this.errorMessageContainer && this.errorMessage) {
        this.errorMessageContainer.nativeElement.style.display = 'flex';
        this.errorMessage.nativeElement.innerText =
          'This song has no lyrics! Please try a different one.';
      }
      this.UIState = 'noPermissions';
      this.loaded = true;
      this.changeUIState();
      Bugsnag.notify('Recorder load error: no lyrics');
      return;
    }
    this.hookUpEvents();
    this.recorder.init();
  }
  hookUpEvents() {
    let userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.indexOf('firefox', 0) !== -1) {
      this.recorder.filters.splice(1, 2);
    }
    if (this.song.duet) {
      this.containerOpen = true;
    }
    //song is loaded!
    this.recorder.addEventListener('initDom', () => this.initDom());
    this.recorder.addEventListener('parts_selected', () =>
      this.partsSelected()
    );
    this.recorder.addEventListener('playheadUpdate', () =>
      this.playheadUpdate()
    ); // playhead is in a new spot, update the scrubber
    this.recorder.addEventListener('input_sources_ready', (r) => {
      this.sources = this.recorder.sources;
      this.onInputSourcesReady(this.recorder.sources);
      const cameraOn = !this.recorder.recorder.noCam;
      this.filters = cameraOn;
      this.cam = cameraOn;
      // Restore the mirror preference so it's baked into the recorded canvas
      this.recorder.flip = this.videoFlip;
      // Activate the draw chain so a restored Video Effect renders immediately.
      this.patchComputeFrameForVibes();
      if (cameraOn) {
        this.recVideo.nativeElement.style.removeProperty('background-image');
        this.recVideo.nativeElement.style.opacity = 0;
        this.filterCanvas.nativeElement.style.opacity = 10;
      } else {
        this.setRecVideoAudioBg(0.4);
        this.filterCanvas.nativeElement.style.opacity = 0;
      }
      // Superpowered's AudioContext may still be suspended in Firefox — resume it now
      // that the user has granted mic permission (counts as a user-gesture unlock).
      const spCtx = (this.recorder as any)?.superPlayer?.webAudio?.audioContext;
      if (spCtx && spCtx.state !== 'running') spCtx.resume().catch(() => {});
      // Restore boost state from previous session
      if (localStorage.getItem('ssMicBoostOn') === 'true') {
        const saved = parseFloat(localStorage.getItem('ssMicBoost') || '0');
        const isFirefox = /Firefox/.test(navigator.userAgent);
        const boostValue = (saved > 0 && !isNaN(saved)) ? saved : (isFirefox ? 20 : 5);
        this.micBoostOn = true;
        this.micBoostLabel = this.gainToDb(boostValue);
        this.recorder.recorder.applyBoostChain(boostValue).catch(() => {
          this.micBoostOn = false;
          this.micBoostLabel = null;
        });
      }
      // Sync device labels to RecorderStore for new UI components
      this.recorderStore.setCamera(this.selectedCamLabel);
      this.recorderStore.setMic(this.selectedMicLabel);
      this.detectChanges();
    });
    this.recorder.addEventListener('show_message', (event, error) => {
      this.toastr.info(error);
      this.detectChanges();
    });
    this.recorder.addEventListener('hide_canvas', () => {
      // this.recorder.recorder.videoOff();
      // this.cam = false
      // this.recVideo.nativeElement.style.opacity = 10
    });
    this.recorder.addEventListener('no_permissions', (event, error) => {
      let msg = error.toString();
      if (this.errorMessageContainer) this.errorMessageContainer.nativeElement.style.display = 'flex';
      if (msg.toUpperCase().includes('PERMISSION DENIED')) {
        msg =
          'Permission Denied by Operating System. Please check that your system is allowing mic and camera access for your browser.';
      }
      if (this.errorMessage) this.errorMessage.nativeElement.innerText = msg;
      // this.UIState = 'noPermissions'
      // this.changeUIState()
      Bugsnag.notify('Recorder: ' + msg);
    });

    //this.recorder.addEventListener('startScrubber', ()=>this.startScrubber())
    this.recorder.addEventListener('durationUpdate', () =>
      this.durationUpdate()
    );
    this.recorder.addEventListener('boostApplied', (event, value: number) => {
      this.micBoostOn    = value !== 1;
      this.micBoostLabel = value !== 1 ? this.gainToDb(value) : null;
      this.detectChanges();
    });
  }
  initDom() {
    this.duration = this.recorder.superPlayer.duration;
    this.loaded = true;
    setTimeout(() => this.moveMicMeter(), 0);
    if (this.pitch) {
      this.onPitchChange(this.pitch);
    }
    if (typeof this.filterIndex != 'undefined') {
      this.changeFilter(this.filterIndex, this.filterName);
    }
    if (typeof this.params.frameName != 'undefined') {
      this.changeFrame(this.params.frameName);
    }
    if (this.musicVolume != 1) {
      this.recorder.changeMusicVolume(Number(this.musicVolume));
    }
    // Show part selector only after recorder is ready (not during loading)
    const songData = this.jam ? this.song?.recordingData : this.song;
    if (songData?.parts) {
      this.initPartSelector(songData);
    }
    this.detectChanges();
    if (this.jam) {
      this.changeDuetStyles();
    }
    if (!this.recorder.recorder.noCam) {
      //this.recorder.startCanvas()
    }
  }
  getIndex() {
    return 1;
    // if(typeof this.ngxGlide != 'undefined') {
    //   return this.ngxGlide.getIndex()
    // } else {
    //   return 1
    // }
  }
  changeFilter(j, name) {
    this.recorder.filterIndex = j;
    this.recorder.filters.forEach((filter, i) => {
      this.recorder.filters[i].selected = '#292C3E';
    });
    this.recorder.filters[j].selected = '#4FEAAE';
    this.filterName = name;
    this.filterIndex = j;
    this.detectChanges();
  }
  openFilterMenu() {
    this.filterMenu = true;
    this.containerOpen = true;
    this.detectChanges();
  }
  closeFilterMenu() {
    this.filterMenu = false;
    this.containerOpen = false;
    this.detectChanges();
  }

  // changeFrame(name) {
  //   let oldName = this.recorder.selectedFrame.name;
  //   if (this.recorder.frames[oldName].promo == true) {
  //     this.recorder.frames[oldName].selected = '#7E7558';
  //   } else {
  //     this.recorder.frames[oldName].selected = '#292C3E';
  //   }
  //   this.recorder.selectedFrame = this.recorder.frames[name];
  //   if (this.recorder.frames[name].promo == true) {
  //     this.recorder.frames[name].selected = '#FFDD5B';
  //   } else {
  //     this.recorder.frames[name].selected = '#4FEAAE';
  //   }
  //   this.detectChanges();
  // }


  changeFrame(frameName: string) {
    const oldName = this.recorder.selectedFrame.name;

    // reset the previous frame color
    if (this.recorder.frames[oldName].promo === true) {
      this.recorder.frames[oldName].selected = '#7E7558';
    } else {
      this.recorder.frames[oldName].selected = '#292C3E';
    }

    // update the new selected frame
    this.recorder.selectedFrame = this.recorder.frames[frameName];

    if (this.recorder.frames[frameName].promo === true) {
      this.recorder.frames[frameName].selected = '#FFDD5B';
    } else {
      this.recorder.frames[frameName].selected = '#4FEAAE';
    }

    this.cdr.detectChanges(); // ✅ update UI
  }
  openFramesMenu() {
    this.framesMenu = true;
    this.containerOpen = true;
    this.detectChanges();
  }
  closeFramesMenu() {
    this.framesMenu = false;
    this.containerOpen = false;
    this.detectChanges();
  }

  nextSlide() {
    // console.log(`---- Recd: ToNextSlide`);
    // let maxPage = Math.round(this.framesPaths.length / 5) * 5
    // if(this.framesMenu) {
    //   if(this.getIndex() >= maxPage) {
    //     this.ngxGlide.go('=0')
    //   } else {
    //     this.ngxGlide.go(`=${this.getIndex() + 5}`)
    //   }
    // } else {
    //   if(this.getIndex() >= 5) {
    //     this.ngxGlide.go('=0')
    //   } else {
    //     this.ngxGlide.go(`=${this.getIndex() + 5}`)
    //   }
    // }
  }
  prevSlide() {
    // console.log(`---- Recd: ToPrevSlide`);
    // let maxPage = Math.round(this.framesPaths.length / 5) * 5
    // if(this.framesMenu) {
    //   if(this.getIndex() <= 0) {
    //     this.ngxGlide.go(`=${maxPage}`)
    //   } else {
    //     this.ngxGlide.go(`=${this.getIndex() - 5}`)
    //   }
    // } else {
    //   if(this.getIndex() <= 0) {
    //     this.ngxGlide.go('=5')
    //   } else {
    //     this.ngxGlide.go(`=${this.getIndex() - 5}`)
    //   }
    // }
  }
  changeFilterItems() {
    this.recorder.filterString = '';
    this.filterItems.forEach((item) => {
      let value = (<HTMLInputElement>document.getElementById(item.id)).value;
      if (typeof value != 'undefined') {
        item.value = value;
      }
      this.recorder.filterString += item.nameStart + item.value + item.nameEnd;
    });
  }
  changeDuetStyles() {
    if (this.song.song.webcam) {
      this.duetVideo.nativeElement.poster = '';
    }

    // Add class — all sizing is handled by CSS (.is-duet rules in SCSS)
    const videoContainer = document.getElementById('videoContainer');
    if (videoContainer) {
      videoContainer.classList.add('is-duet');
      videoContainer.style.removeProperty('width');
    }

    // Clear any previously set inline styles so CSS rules are not overridden
    this.duetVideo.nativeElement.style.removeProperty('display');
    this.duetVideo.nativeElement.style.removeProperty('width');
    this.duetVideo.nativeElement.style.removeProperty('height');
    this.duetVideo.nativeElement.style.removeProperty('flex-shrink');

    const filtersCanvas = document.getElementById('filters');
    if (filtersCanvas) {
      filtersCanvas.style.removeProperty('left');
      filtersCanvas.style.removeProperty('right');
      filtersCanvas.style.removeProperty('transform');
      filtersCanvas.style.removeProperty('width');
      filtersCanvas.style.removeProperty('height');
    }

    this.detectChanges();
  }
  partsSelected() {
    if (typeof this.recorder.payload != 'undefined') {
      this.part = this.recorder.payload.part;
    }
    this.containerOpen = false;
  }
  purchaseGold() {
    this.router.navigate(['/d/payments/membership']);
  }
  // Show (or suppress) the audio-only placeholder background on #recVideo.
  // In a duet the .no-cam-overlay <img> — constrained to the 50% half by the
  // .is-duet rules — is the placeholder, so #recVideo must stay fully transparent;
  // otherwise its full-width background bleeds beside the split as a stray oversized
  // image duplicating the img. Non-duet keeps the original dimmed-background look.
  private setRecVideoAudioBg(opacity: number) {
    if (this.jam) {
      this.recVideo.nativeElement.style.removeProperty('background-image');
      this.recVideo.nativeElement.style.opacity = 0;
    } else {
      this.recVideo.nativeElement.style.backgroundImage = 'url(../../assets/images/audio_only.png)';
      this.recVideo.nativeElement.style.opacity = opacity;
    }
  }
  // Mirror the camera horizontally. The flip is applied in the engine's
  // computeFrame (which draws the recorded canvas), so it bakes into the saved
  // video and shows in review — not just a preview transform. Remembered per-user.
  toggleFlip() {
    this.videoFlip = !this.videoFlip;
    localStorage.setItem('ssVideoFlip', String(this.videoFlip));
    if (this.recorder && typeof this.recorder != 'string') {
      this.recorder.flip = this.videoFlip;
    }
    this.detectChanges();
  }

  // ── Vibes ──────────────────────────────────────────────────────────────────
  // Expand/collapse the Vibes dropdown in the Presentation panel (mirrors
  // Filters/Frames). Remembered.
  toggleVibesExpanded(): void {
    this.vibesExpanded = !this.vibesExpanded;
    if (this.vibesExpanded) {
      this._scrollSectionToTop(this.vibesToggle);
    }
    localStorage.setItem('ssVibesExpanded', String(this.vibesExpanded));
    this.detectChanges();
  }

  // Show/hide the moveable Vibes box (toggled from the Presentation panel or the
  // box's own close button). Remembered.
  toggleVibesBox(): void {
    this.vibesBoxOpen = !this.vibesBoxOpen;
    localStorage.setItem('ssVibesBoxOpen', String(this.vibesBoxOpen));
    this.detectChanges();
  }

  // Switch the box between a horizontal (wrap row) and vertical (column) layout.
  toggleVibesBoxOrient(): void {
    this.vibesBoxVertical = !this.vibesBoxVertical;
    localStorage.setItem('ssVibesBoxVertical', String(this.vibesBoxVertical));
    this.detectChanges();
  }

  // Box transparency (slider in the box handle). Remembered.
  onBoxOpacity(v: number): void {
    this.vibesBoxOpacity = v;
    localStorage.setItem('ssVibesBoxOpacity', String(v));
  }

  // ── Moveable box drag (via its handle only; chips stay clickable) ───────────
  onBoxDown(e: PointerEvent): void {
    this.boxDragging = true;
    const rect = this.recorderContainer?.nativeElement.getBoundingClientRect();
    if (rect) {
      this.boxGrabDX = e.clientX - rect.left - (this.vibesBoxX / 100) * rect.width;
      this.boxGrabDY = e.clientY - rect.top - (this.vibesBoxY / 100) * rect.height;
    }
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    e.preventDefault();
  }

  onBoxMove(e: PointerEvent): void {
    if (!this.boxDragging) return;
    const rect = this.recorderContainer?.nativeElement.getBoundingClientRect();
    if (!rect) return;
    let xp = ((e.clientX - rect.left - this.boxGrabDX) / rect.width) * 100;
    let yp = ((e.clientY - rect.top - this.boxGrabDY) / rect.height) * 100;
    this.vibesBoxX = Math.max(0, Math.min(88, xp));
    this.vibesBoxY = Math.max(2, Math.min(92, yp));
  }

  onBoxUp(e: PointerEvent): void {
    if (!this.boxDragging) return;
    this.boxDragging = false;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    localStorage.setItem('ssVibesBoxX', String(this.vibesBoxX));
    localStorage.setItem('ssVibesBoxY', String(this.vibesBoxY));
  }

  // Fire a burst. Spawns its particles now and records the start time; the burst
  // animates + bakes into the recording via the computeFrame chain below.
  fireVibe(vibe: Vibe) {
    this.patchComputeFrameForVibes();
    this.activeBursts.push({
      vibe,
      startTime: performance.now(),
      particles: vibe.spawn(),
    });
  }

  // Monkey-patch the engine's computeFrame so active bursts draw onto the same
  // 600×600 filterCanvas that Filters/Frames write to — the captureStream source.
  // That's what makes bursts appear in the live preview AND bake into the MP4.
  // Patched once, lazily, on first fire (computeFrame/filterCtx exist by then).
  private patchComputeFrameForVibes() {
    const k: any = this.recorder;
    if (!k || this.vibesPatched || typeof k.computeFrame !== 'function') return;
    const original = k.computeFrame.bind(k);
    const self = this;
    k.computeFrame = function () {
      original();
      const ctx = k.filterCtx;
      if (!ctx) return;
      self.drawSelectedEffect(ctx); // persistent overlay under the bursts
      self.drawActiveBursts(ctx);
    };
    this.vibesPatched = true;
  }

  // Draw the selected persistent video effect (Effects section). Runs every frame.
  private drawSelectedEffect(ctx: CanvasRenderingContext2D) {
    const eff = this.videoEffects.find((e) => e.id === this.selectedEffectId);
    if (!eff || !eff.draw) return;
    ctx.save();
    try {
      eff.draw(ctx, performance.now() / 1000);
    } catch {}
    ctx.restore();
  }

  get selectedEffectName(): string {
    return this.videoEffects.find((e) => e.id === this.selectedEffectId)?.name ?? 'None';
  }

  // Select a video effect (persistent, single-select like Filters/Frames).
  selectEffect(id: string): void {
    this.selectedEffectId = id;
    localStorage.setItem('ssVideoEffect', id);
    this.patchComputeFrameForVibes(); // ensure the draw chain is active
    this.detectChanges();
  }

  toggleEffectsExpanded(): void {
    this.effectsExpanded = !this.effectsExpanded;
    if (this.effectsExpanded) {
      this._scrollSectionToTop(this.effectsToggle);
    }
    localStorage.setItem('ssEffectsExpanded', String(this.effectsExpanded));
    this.detectChanges();
  }

  // Draw (and expire) every active burst. Called each filter-loop frame.
  private drawActiveBursts(ctx: CanvasRenderingContext2D) {
    if (!this.activeBursts.length) return;
    const now = performance.now();
    this.activeBursts = this.activeBursts.filter(
      (b) => now - b.startTime <= b.vibe.durationMs
    );

    // macOS-style backdrop: dim/tint the video BEHIND the particles so the effect
    // pops. Draw only the STRONGEST active backdrop ONCE (not per burst) so firing
    // a vibe repeatedly can't stack the dims to solid black. Hard-capped so the
    // video is never more than partly faded.
    const MAX_BACKDROP = 0.45;
    let bestAlpha = 0;
    let bestColor = '';
    for (const b of this.activeBursts) {
      const bd = b.vibe.backdrop;
      if (!bd) continue;
      const prog = (now - b.startTime) / b.vibe.durationMs;
      const env = Math.min(1, prog / 0.12) * Math.max(0, Math.min(1, (1 - prog) / 0.3));
      const a = bd.peak * env;
      if (a > bestAlpha) {
        bestAlpha = a;
        bestColor = bd.color;
      }
    }
    if (bestAlpha > 0.01) {
      ctx.save();
      ctx.globalAlpha = Math.min(bestAlpha, MAX_BACKDROP);
      ctx.fillStyle = bestColor;
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.restore();
    }

    for (const b of this.activeBursts) {
      const tSec = (now - b.startTime) / 1000;
      ctx.save();
      try {
        b.vibe.draw(ctx, b.particles, tSec);
      } catch {}
      ctx.restore();
    }
  }

  toggleCamera() {
    if (this.recorder.recorder.noCam) {
      localStorage.removeItem('noCam');
      this.recVideo.nativeElement.style.removeProperty('background-image');
      this.recorder.recorder.videoOn();
      this.recVideo.nativeElement.style.opacity = 0;
      this.filterCanvas.nativeElement.style.opacity = 10;
      this.filters = true;
      this.cam = true;
    } else {
      // Stop video tracks immediately so the camera light turns off now
      const stream = this.recorder?.recorder?.stream;
      if (stream) {
        stream.getVideoTracks().forEach((t: MediaStreamTrack) => t.stop());
      }
      // Clear the frozen last frame — stopped tracks leave it in the element
      this.recVideo.nativeElement.srcObject = null;
      // Persist noCam synchronously so Try Again picks it up even if fired immediately
      localStorage.setItem('noCam', 'true');
      localStorage.removeItem('videoDevice');
      this.recorder.recorder.videoOff();
      this.filterCanvas.nativeElement.style.opacity = 0;
      this.setRecVideoAudioBg(1);
      this.cam = false;
    }
    this.detectChanges();
  }
  // changeCamSource(deviceId) {
  //   this.detectChanges();

  //   this.recorder.recorder.changeVideoDevice(deviceId);
  //   this.videoDropDown = false;
  //   this.detectChanges();
  // }
  // changeMicSource(deviceId) {
  //   this.recorder.recorder.changeAudioDevice(deviceId);
  //   this.micDropDown = false;
  //   this.detectChanges();
  // }
  changeMicSource(deviceId: string): void {
  this.activeAudioDeviceId = deviceId;
  this.micDropDown = false;
  Cookie.set('recorder-audio-device-id', deviceId);
  this.recorder.recorder.changeAudioDevice(deviceId);
  (window as any).__ssRecorderAudioDeviceId = deviceId;
  // Refresh the stream reference after the device switch settles
  setTimeout(() => {
    (window as any).__ssRecorderStream = this.recorder?.recorder?.stream ?? null;
  }, 500);
  setTimeout(() => { this.recorderStore.setMic(this.selectedMicLabel); }, 100);
  this.detectChanges();
}

changeCamSource(deviceId: string): void {
  this.activeVideoDeviceId = deviceId;
  this.videoDropDown = false;
  Cookie.set('recorder-video-device-id', deviceId);
  this.detectChanges();
  this.recorder.recorder.changeVideoDevice(deviceId);
  this.detectChanges();
  setTimeout(() => { this.recorderStore.setCamera(this.selectedCamLabel); }, 100);
}


  toggleVideoDropDown() {
    this.videoDropDown = !this.videoDropDown;
    if (this.videoDropDown) this.micDropDown = false;
    this.detectChanges();
  }
  toggleMicDropDown() {
    if (this.UIState == 'recording' || this.UIState == 'countdown') return;
    this.micDropDown = !this.micDropDown;
    if (this.micDropDown) this.videoDropDown = false;
    this.detectChanges();
  }

  // ── Device listbox keyboard navigation ─────────────────────────────────────

  private openCamListbox(): void {
    this.videoDropDown = true;
    this.micDropDown = false;
    this.detectChanges();
    setTimeout(() => {
      const first = document.querySelector<HTMLElement>('#cam-listbox [role="option"]');
      first?.focus();
    }, 0);
  }

  private openMicListbox(): void {
    if (this.UIState === 'recording' || this.UIState === 'countdown') return;
    this.micDropDown = true;
    this.videoDropDown = false;
    this.detectChanges();
    setTimeout(() => {
      const first = document.querySelector<HTMLElement>('#mic-listbox [role="option"]');
      first?.focus();
    }, 0);
  }

  onCamTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.openCamListbox();
    }
  }

  onMicTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.openMicListbox();
    }
  }

  onCamListKeydown(event: KeyboardEvent): void {
    const list = event.currentTarget as HTMLElement;
    const items = Array.from(list.querySelectorAll<HTMLElement>('[role="option"]'));
    const idx = items.indexOf(document.activeElement as HTMLElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        items[Math.min(idx + 1, items.length - 1)]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (idx <= 0) {
          this.videoDropDown = false;
          this.detectChanges();
          this.camTriggerBtn?.nativeElement.focus();
        } else {
          items[idx - 1]?.focus();
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        (document.activeElement as HTMLElement)?.click();
        break;
      case 'Escape':
        event.preventDefault();
        this.videoDropDown = false;
        this.detectChanges();
        this.camTriggerBtn?.nativeElement.focus();
        break;
      case 'Tab':
        this.videoDropDown = false;
        this.detectChanges();
        break;
      case 'Home':
        event.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        items[items.length - 1]?.focus();
        break;
    }
  }

  onMicListKeydown(event: KeyboardEvent): void {
    const list = event.currentTarget as HTMLElement;
    const items = Array.from(list.querySelectorAll<HTMLElement>('[role="option"]'));
    const idx = items.indexOf(document.activeElement as HTMLElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        items[Math.min(idx + 1, items.length - 1)]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (idx <= 0) {
          this.micDropDown = false;
          this.detectChanges();
          this.micTriggerBtn?.nativeElement.focus();
        } else {
          items[idx - 1]?.focus();
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        (document.activeElement as HTMLElement)?.click();
        break;
      case 'Escape':
        event.preventDefault();
        this.micDropDown = false;
        this.detectChanges();
        this.micTriggerBtn?.nativeElement.focus();
        break;
      case 'Tab':
        this.micDropDown = false;
        this.detectChanges();
        break;
      case 'Home':
        event.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        items[items.length - 1]?.focus();
        break;
    }
  }
  // ── InlineTick helpers ────────────────────────────────────────────────────────

  get musicVolumeDisplay(): number {
    return Math.round(this.musicVolume * 50);
  }

  get micVolumeDisplay(): number {
    return Math.round(this.micVolume * 50);
  }

  onMicVolumeSliderChange(v: number): void {
    this.micVolume = v;
    if (this.recorder) this.recorder.changeVocalVolume(this.micVolume);
    this.detectChanges();
  }

  onSyncSliderChange(v: number): void {
    this.onSyncChange(v);
  }

  // Live pill/fill update while dragging; the real apply happens on release (change).
  onSyncSliderInput(v: number): void {
    this.sync = v;
    this.detectChanges();
  }

  // ── Sync pill click-to-edit ────────────────────────────────────────────────
  // Slider step is 10ms so precise values like -47ms aren't reachable via drag.
  // Clicking the value pill swaps it for a number input; Enter/blur commits,
  // Escape cancels. Value clamped to slider range.
  syncEditing = false;
  syncDraft: number | null = null;
  @ViewChild('syncInput') private syncInput?: ElementRef<HTMLInputElement>;

  startSyncEdit(): void {
    this.syncDraft = this.sync;
    this.syncEditing = true;
    requestAnimationFrame(() => {
      const el = this.syncInput?.nativeElement;
      if (el) { el.focus(); el.select(); }
    });
  }

  commitSyncEdit(raw: string | number): void {
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (!isNaN(n)) {
      this.onSyncChange(Math.max(-500, Math.min(500, n)));
    }
    this.syncEditing = false;
  }

  cancelSyncEdit(): void {
    this.syncEditing = false;
  }

  // ── Generic value-pill click-to-edit ───────────────────────────────────────
  // Every value pill (volume, pitch, contrast, lyric size) can be clicked to
  // type an exact value the slider step can't always reach. One mechanism keyed
  // by pill name; only one pill edits at a time. (Sync keeps its own handlers.)
  editingPill: string | null = null;
  pillDraft: number | null = null;
  @ViewChildren('pillInput') private pillInputs?: QueryList<ElementRef<HTMLInputElement>>;

  get canEditPitch(): boolean {
    return !!this.UI?.pitchButton && !this.jam;
  }

  startPillEdit(name: string, current: number): void {
    this.pillDraft = current;
    this.editingPill = name;
    requestAnimationFrame(() => {
      // Only the editing pill's input(s) are in the DOM; focus the visible one
      // (music volume renders in both the setup and review panels).
      const inputs = this.pillInputs?.toArray() ?? [];
      const el = inputs.find(e => e.nativeElement.offsetParent !== null)?.nativeElement
               ?? inputs[0]?.nativeElement;
      if (el) { el.focus(); el.select(); }
    });
  }

  commitPillEdit(name: string, raw: string | number, min: number, max: number): void {
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (!isNaN(n)) {
      const clamped = Math.max(min, Math.min(max, n));
      switch (name) {
        case 'music':     this.onInlineTickVolumeChange(clamped / 50); break; // 0–100 display → 0–2 gain
        case 'vocal':     this.onMicVolumeSliderChange(clamped / 50); break;
        case 'pitch':     this.onInlineTickPitchChange(clamped); break;
        case 'contrast':  this.onContrastChange(clamped); break;
        case 'lyricSize': this.onLyricSizeChange(clamped); break;
      }
    }
    this.editingPill = null;
  }

  cancelPillEdit(): void {
    this.editingPill = null;
  }

  // ── "Send diagnostics to SingSnap" ─────────────────────────────────────────
  // Support-directed link in the Review panel. Opens a required note field + a
  // read-only diagnostic snapshot, then sends it as an in-app message to the
  // SingSnapSupport account (env.supportUserId). Copy is a fallback.
  reportOpen = false;
  reportNote = '';
  reportSetup = '';
  reportCopied = false;
  reportSending = false;
  reportSent = false;
  reportError = false;

  openReport(): void {
    this.reportNote = '';
    this.reportCopied = false;
    this.reportSent = false;
    this.reportError = false;
    this.reportSending = false;
    this.reportSetup = this.buildReportSetup();
    this.reportOpen = true;
    this.detectChanges();
  }

  cancelReport(): void {
    this.reportOpen = false;
    this.detectChanges();
  }

  // Human-readable setup snapshot (no note — that's the field above it).
  private buildReportSetup(): string {
    const boostOn = localStorage.getItem('ssMicBoostOn') === 'true';
    const boost = parseFloat(localStorage.getItem('ssMicBoost') || '1');
    const ctxState = (window as any).__ssRecorderAudioCtx?.state ?? 'unknown';
    const user = this.profileService?.user;
    const audioDeviceName = this.sources?.audio?.find((s: any) => s.deviceId === this.activeAudioDeviceId)?.label ?? '(unknown)';
    const videoDeviceName = this.sources?.video?.find((s: any) => s.deviceId === this.activeVideoDeviceId)?.label ?? '(none / unknown)';
    const reporter = user?.screen_name || (user?.id != null ? `id:${user.id}` : 'Unknown user');
    return [
      `Reporter: ${reporter}${user?.id != null ? ` (id ${user.id})` : ''}`,
      `Song: ${this.title ?? '(unknown)'} (id ${this.id ?? '?'})`,
      `Song link: ${window.location.href}`,
      `Camera on: ${this.cam ? 'yes' : 'no'} | Duet: ${this.jam ? 'yes' : 'no'}`,
      `Microphone: ${audioDeviceName}`,
      `Camera: ${videoDeviceName}`,
      `Vocal volume: ${this.micVolumeDisplay} | Music volume: ${this.musicVolumeDisplay}`,
      `Sync: ${this.sync} | Pitch: ${this.pitch}`,
      `Mic boost: ${boostOn ? boost : 1}${boostOn ? ' (on)' : ' (off)'}`,
      `Audio engine: ${ctxState === 'running' ? 'active' : ctxState === 'suspended' ? 'BLOCKED (browser paused audio)' : ctxState === 'closed' ? 'closed' : 'unknown (' + ctxState + ')'}`,
      `Browser: ${navigator.userAgent}`,
    ].join('\n');
  }

  // Note + setup combined — what gets sent / copied.
  private fullReportText(): string {
    const note = (this.reportNote || '').trim() || '(not specified)';
    return `Issue: ${note}\n\n${this.reportSetup}`;
  }

  // Send the diagnostics as an in-app message to SingSnapSupport (env-specific id).
  sendToSupport(): void {
    if (!this.reportNote?.trim() || this.reportSending) { return; }
    this.reportSending = true;
    this.reportError = false;
    this.detectChanges();
    // Message bodies are Quill HTML (one <p> per line), so wrap each event line
    // in its own paragraph and escape HTML so URLs/user-agent don't break it.
    // (Copy stays plain text.)
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const body = this.fullReportText().split('\n').map(l => `<p>${esc(l)}</p>`).join('');
    // SingSnapSupport account id — prod 247 / dev 321. Derived from the existing
    // production flag so we don't have to touch the environment config files.
    const supportUserId = environment.production ? 247 : 321;
    this.messageService.sendMessage(supportUserId, body).subscribe(
      () => {
        this.reportSending = false;
        this.reportSent = true;
        this.reportOpen = false;
        this.detectChanges();
        setTimeout(() => { this.reportSent = false; this.detectChanges(); }, 4000);
      },
      () => {
        // Leave the box open so they can Copy and email as a fallback.
        this.reportSending = false;
        this.reportError = true;
        this.detectChanges();
      }
    );
  }

  copyReport(): void {
    if (!this.reportNote?.trim()) { return; }   // require a description
    const text = this.fullReportText();
    try {
      navigator.clipboard?.writeText(text);
    } catch (e) {
      // Clipboard may be unavailable; the box is selectable as a fallback.
    }
    this.reportCopied = true;
    this.detectChanges();
    setTimeout(() => {
      this.reportCopied = false;
      this.detectChanges();
    }, 2500);
  }


  sliderBg(value: number, min: number, max: number): string {
    const pct = ((value - min) / (max - min)) * 100;
    return `linear-gradient(to right, #58c7c9 ${pct}%, rgba(232,236,243,0.12) ${pct}%)`;
  }

  get isGold(): boolean {
    return !!this.profileService.user?.gold;
  }

  toggleLyrics(): void {
    this.lyricsVisible = !this.lyricsVisible;
    this.detectChanges();
  }

  onInlineTickPitchChange(v: number): void {
    this.pitch = v;
    if (this.recorder) this.recorder.changePitch(v);
    this.detectChanges();
  }

  onInlineTickVolumeChange(v: number): void {
    this.musicVolume = v;
    if (this.recorder) this.recorder.changeMusicVolume(this.musicVolume);
    this.detectChanges();
  }

  // ── Thumb-row helpers (filters + frames in right panel) ──────────────────────

  get selectedFilterKey(): string {
    return String(this.filterIndex ?? 0);
  }

  private readonly THUMB_PREVIEW_URL = 'assets/images/video_fx_preview.jpeg';

  get filterThumbItems(): ThumbItem[] {
    if (!this.recorder?.filters) return [];
    return this.recorder.filters.map((f: any, i: number) => ({
      key: String(i),
      name: f.name ?? String(i),
      thumbUrl: this.THUMB_PREVIEW_URL,
    }));
  }

  get frameThumbItems(): ThumbItem[] {
    return this.framesPaths.map(f => ({
      key: f.name,
      name: f.name,
      thumbUrl: this.THUMB_PREVIEW_URL,
    }));
  }

  onFilterThumbChange(key: string): void {
    const idx = parseInt(key, 10);
    if (!this.recorder?.filters || isNaN(idx)) return;
    const filter = this.recorder.filters[idx];
    if (filter) {
      this.changeFilter(idx, filter.name);
      this.filterFocusIndex = idx;
      // Grid stays open — user can preview more options; Enter or toggle closes
    }
  }

  onFrameThumbChange(key: string): void {
    this.currentFrameName = key;
    if (this.recorder) this.changeFrame(key);
    const fi = this.framesPaths.findIndex(f => f.name === key);
    if (fi >= 0) this.frameFocusIndex = fi;
    // Grid stays open — user can preview more options; Enter or toggle closes
  }

  toggleThemes(): void {
    this.themesExpanded = !this.themesExpanded;
    if (this.themesExpanded) {
      this._scrollSectionToTop(this.themesToggle);
    }
  }

  commitThemes(): void {
    this.themesExpanded = false;
    setTimeout(() => this.themesToggle?.nativeElement.focus());
  }

  toggleFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
    if (this.filtersExpanded) {
      this.filterFocusIndex = this.filterIndex ?? 0;
      this.filterOriginalIndex = this.filterIndex ?? 0;
      this._scrollSectionToTop(this.filtersToggle);
    }
    localStorage.setItem('ssFiltersExpanded', String(this.filtersExpanded));
  }

  toggleFrames(): void {
    this.framesExpanded = !this.framesExpanded;
    if (this.framesExpanded) {
      const fi = this.framesPaths.findIndex(f => f.name === this.currentFrameName);
      this.frameFocusIndex = fi >= 0 ? fi : 0;
      this.frameOriginalName = this.currentFrameName;
      this._scrollSectionToTop(this.framesToggle);
    }
    localStorage.setItem('ssFramesExpanded', String(this.framesExpanded));
  }

  /**
   * When the user expands Filters or Frames in the right panel, scroll the
   * panel so the section header sits at the top — keeps the action visible
   * because the grid that just opened often pushes content out of view.
   */
  private _scrollSectionToTop(ref: ElementRef<HTMLButtonElement> | undefined): void {
    const btn = ref?.nativeElement;
    if (!btn) return;
    // Wait one frame so the grid renders and the panel measures correctly.
    requestAnimationFrame(() => {
      btn.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }

  /** Performance-strip carousels — mutually exclusive: opening one closes the other. */
  // Note: commit*/revert* intentionally do NOT persist to localStorage.
  // Those are autoclose paths (click-outside / Escape) — the persisted
  // state should reflect the user's explicit open/close via the chevron
  // in toggleFilters/toggleFrames, so "open when I leave" survives the
  // window-click autoclose that fires when navigating away.
  commitFilters(): void {
    this.filtersExpanded = false;
    setTimeout(() => this.filtersToggle?.nativeElement.focus());
  }

  private revertFilters(): void {
    if (this.filterOriginalIndex !== null) {
      const filter = this.recorder?.filters?.[this.filterOriginalIndex];
      if (filter) this.changeFilter(this.filterOriginalIndex, filter.name);
      this.filterFocusIndex = this.filterOriginalIndex;
    }
    this.filtersExpanded = false;
    setTimeout(() => this.filtersToggle?.nativeElement.focus());
  }

  commitFrames(): void {
    this.framesExpanded = false;
    setTimeout(() => this.framesToggle?.nativeElement.focus());
  }

  private revertFrames(): void {
    if (this.frameOriginalName !== null) {
      this.currentFrameName = this.frameOriginalName;
      if (this.recorder) this.changeFrame(this.frameOriginalName);
      const fi = this.framesPaths.findIndex(f => f.name === this.frameOriginalName);
      if (fi >= 0) this.frameFocusIndex = fi;
    }
    this.framesExpanded = false;
    setTimeout(() => this.framesToggle?.nativeElement.focus());
  }

  onFxGridKeydown(event: KeyboardEvent, grid: 'filter' | 'frame', totalItems: number): void {
    const { key } = event;
    if (key === 'Escape') {
      event.preventDefault();
      if (grid === 'filter') this.revertFilters();
      else this.revertFrames();
      return;
    }
    if (key === 'Enter') {
      event.preventDefault();
      if (grid === 'filter') this.commitFilters();
      else this.commitFrames();
      return;
    }
    // Space is reserved for the global recorder start/stop — prevent the focused button
    // from consuming it while still letting the event bubble to the window handler.
    if (key === ' ') { event.preventDefault(); return; }
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) return;
    event.preventDefault();
    const cols = 3;
    let idx = grid === 'filter' ? this.filterFocusIndex : this.frameFocusIndex;
    if (key === 'ArrowRight') idx = Math.min(idx + 1, totalItems - 1);
    else if (key === 'ArrowLeft') idx = Math.max(idx - 1, 0);
    else if (key === 'ArrowDown') idx = Math.min(idx + cols, totalItems - 1);
    else if (key === 'ArrowUp') idx = Math.max(idx - cols, 0);
    else if (key === 'Home') idx = 0;
    else if (key === 'End') idx = totalItems - 1;
    if (grid === 'filter') this.filterFocusIndex = idx;
    else this.frameFocusIndex = idx;
    const buttons = (event.currentTarget as HTMLElement).querySelectorAll<HTMLElement>('[role="gridcell"]');
    buttons[idx]?.focus();
  }

  get storeFillLightStyle(): Record<string, string> {
    const fl = this.recorderStore.fillLight();

    // Opacity-based overlay: at brightness=0 the inset box-shadow is fully
    // transparent so the theme background gradient shows through naturally.
    // At brightness=100 the overlay is fully opaque, painting the "full
    // intensity" fill color over everything below child elements.
    const bFactor = fl.brightness / 100;
    let r: number, g: number, b: number;

    if (!fl.toneOn) {
      // Tone Off: full intensity = theme's "lit" endpoint. Themes with an
      // explicit fillLight.light use that; otherwise we derive a lifted
      // version of the theme bg by pushing its HSL lightness near the top
      // while preserving hue/saturation. This gives every theme a
      // brightness-shifts-the-bg-color feel rather than a flat white wash.
      const themed = this.themeService.activeTheme.fillLight;
      if (themed) {
        [r, g, b] = themed.light;
      } else {
        const shellBg = this.themeService.activeTheme.tokens['--shell-bg'] ?? '';
        const hex = shellBg.match(/#[0-9a-fA-F]{6}/)?.[0] ?? '#04071c';
        [r, g, b] = liftToLight(hex);
      }
    } else {
      const t = fl.warmth / 100;
      // Tone on: full intensity = the tone color at this warmth position.
      // Brightness controls opacity, not color saturation, so the color
      // stays consistent as the user slides intensity.
      const { cool, warm } = this.themeService.toneFor(this.themeService.activeTheme);
      const [cR, cG, cB] = cool;
      const [wR, wG, wB] = warm;

      if (t <= 0.5) {
        const f = t * 2;
        r = Math.round(cR + (255 - cR) * f);
        g = Math.round(cG + (255 - cG) * f);
        b = Math.round(cB + (255 - cB) * f);
      } else {
        const f = (t - 0.5) * 2;
        r = Math.round(255 + (wR - 255) * f);
        g = Math.round(255 + (wG - 255) * f);
        b = Math.round(255 + (wB - 255) * f);
      }
    }

    return {
      'box-shadow': `inset 0 0 0 2000px rgba(${r}, ${g}, ${b}, ${bFactor})`,
      '--fill-brightness': String(bFactor),
    };
  }

  backClicked() {
    this.router.navigate(['/d/sing']);
  }
  playheadUpdate() {
    4;
    if (!this.scrubberOn) {
      return;
    }
    if (!this.recorder?.superPlayer) {
      return;
    }
    this.playhead = this.recorder.superPlayer.playhead;
    if (this.UIState === 'practicing') {
      this.practiceElapsed = Math.floor(this.playhead / 1000);
    }
    // A jam records against the previous layer's render — its audio is what sets
    // this.duration (correct: a parent may be shorter than the original track). But
    // any gap we leave before the end makes each layer shorter than its parent, and
    // that loss compounds down the chain (layer 5 ends up seconds short). So for jams
    // stop essentially AT the parent's length instead of 500ms before it, so each
    // layer matches its parent. A small margin (not 0) is kept so the auto-stop still
    // reliably fires on the last position update rather than risking a never-ending
    // recording. Standalone recordings keep the original 500ms tail.
    const endMargin = this.jam ? 100 : 500;
    if (this.playhead >= this.duration - endMargin) {
      if (this.UIState == 'recording') {
        this.stopRecording();
      } else {
        this.pause();
        this.recorder.seek(0);
        this.playhead = 0;
        this.practiceElapsed = 0;
      }
    }
    this.detectChanges();
  }
  msToTime(s) {
    let pad = (n, z = 2) => ('00' + n).slice(-z);
    return pad(((s % 3.6e6) / 6e4) | 0) + ':' + pad(((s % 6e4) / 1000) | 0);
  }
  secsToTime(s: number): string {
    const pad = (n: number) => ('00' + n).slice(-2);
    return pad(Math.floor(s / 60)) + ':' + pad(s % 60);
  }
  startScrubber() {
    this.scrubberOn = true;
    // if(typeof this.interval != 'undefined') {
    //   this.stopScrubber()
    // }
    this.interval = setInterval(() => {
      this.recorder?.superPlayer?.getPosition();
    }, 100);
  }
  stopScrubber() {
    this.scrubberOn = false;
    clearInterval(this.interval);
    this.interval = null;
  }
  changeRangeTime(e) {
    if (this.scrubberOn) {
      this.stopScrubber();
    }
    this.playhead = parseFloat(e);
    this.detectChanges();
  }
  seek(e) {
    if (this.UIState == 'review') {
      this.pause();
    }
    e = parseFloat(e);
    this.recorder.seek(e);
    this.playhead = e;
    if (this.recorder?.superPlayer?.playing && !this.scrubberOn) {
      this.startScrubber();
    }
    this.detectChanges();
  }
  restartPractice() {
    this.seek(0);
    this.practiceElapsed = 0;
    this.UIState = 'default';
    this.changeUIState();
  }
  tryAgain() {
    gtag('event', 'sing', {
      action: 'Try Again',
      songId: this.id.toString(),
    });
    if (this.fullscreen) { sessionStorage.setItem('ssFullscreenRestore', '1'); }
    this.restarting = true;
    let currentUrl;
    //window.location.href = `/?pitch=${this.pitch}&volume=${this.volume}&micVolume=${this.micVolume}`
    if (this.router.url.includes('?')) {
      currentUrl = this.router.url.substr(0, this.router.url.indexOf('?'));
    } else {
      currentUrl = this.router.url;
    }
    this.router.navigate([currentUrl], {
      queryParams: {
        pitch: this.pitch,
        volume: this.musicVolume,
        ...(typeof this.filterIndex != 'undefined' && {
          filterName: this.filterName,
          filterIndex: this.filterIndex,
        }),
        ...(typeof this.recorder.selectedFrame.name != 'undefined' && {
          frameName: this.recorder.selectedFrame.name,
        }),
      },
    });
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }
  startCountdown() {
    this.countdown.nativeElement.style.display = 'flex';
    this.UIState = 'countdown';
    this.changeUIState();
    let interval = setInterval(() => {
      if (this.count == 1) {
        clearInterval(interval);
        this.countdown.nativeElement.style.display = 'none';
        this.startRecording();
        return;
      }
      this.count--;
      this.detectChanges();
    }, 1000);
  }
  startRecording() {
    gtag('event', 'sing', {
      action: 'Record Song',
      songId: this.id.toString(),
    });
    if (this.recorder.record() === false) {
      // Recorder wasn't ready (mic/cam still initializing) — record() showed a
      // message and did not start playback. Return to setup so the user can retry.
      this.count = 5;
      this.UIState = 'default';
      this.changeUIState();
      this.detectChanges();
      return;
    }
    this.playhead = 0;
    this.singService.startRecording(this.id, this.jam).subscribe((result) => {
      this.uuid = result;
    });
    this.UIState = 'recording';
    this.startScrubber();
    this.changeUIState();
  }
  stopRecording() {
    this.singService
      .stopRecording(this.id, this.uuid, this.jam)
      .subscribe((result) => {
      });
    this.pause();
    this.recorder.stopRecording();
    this.stopScrubber();
    this.finished = true;
    this.UIState = 'reviewPaused';
    if (!this.cam) {
      this.recVideo.nativeElement.srcObject = null;
      this.setRecVideoAudioBg(1);
    } else {
      this.recVideo.nativeElement.style.opacity = 10;
    }
    // this.filterCanvas.nativeElement.style.opacity = 0

    this.seek(0);
    this.changeUIState();
  }
  pause() {
    this.recorder.pause();
    if (this.recorder.mode == 'review') {
      this.UIState = 'reviewPaused';
    } else {
      this.UIState = 'practicingPaused';
    }
    this.stopScrubber();
    this.changeUIState();
  }
  play() {
    this.recorder.play();
    if (this.recorder.recording) {
      this.UIState = 'recording';
    } else if (this.recorder.mode == 'review') {
      gtag('event', 'sing', {
        action: 'Review Recording',
        songId: this.id.toString(),
      });
      this.UIState = 'review';
    } else {
      gtag('event', 'sing', {
        action: 'Preview Song',
        songId: this.id.toString(),
      });
      this.UIState = 'practicing';
    }
    this.changeUIState();
    this.startScrubber();
  }
  durationUpdate() {
    this.duration = this.recorder.superPlayer.duration;
    this.seek(0);
    this.playhead = 0;
    let sync = parseInt(localStorage.getItem('sync'));
    if (sync) {
      this.sync = sync;
      this.recorder.sync(this.sync);
    }
    this.toastr.success('Review loaded!');
    this.detectChanges();
  }
  onPitchClick() {
    this.containerOpen = true;
    this.pitchBar = true;
    this.detectChanges();
  }
  onPitchChange(e) {
    this.pitch = e;
    this.recorder.changePitch(e);
  }
  onPitchClose() {
    this.pitchBar = false;
    this.containerOpen = false;
    this.detectChanges();
  }
  onSyncClick() {
    this.containerOpen = true;
    this.syncBar = true;
    this.detectChanges();
  }

  onSyncTestClick(): void {
    if (this.recorder.superPlayer.playing) this.pause();
    this.showSyncModal = true;
    this.detectChanges();
  }

  onSyncModalApplied(value: number): void {
    this.onSyncChange(value);
  }

  onSyncModalClosed(): void {
    this.showSyncModal = false;
    this.detectChanges();
  }

  private gainToDb(gain: number): string {
    const db = 20 * Math.log10(gain);
    const sign = db >= 0 ? '+' : '';
    return `${sign}${Math.round(db * 10) / 10}dB`;
  }

  onAutoMicClick(): void {
    if (this.micBoostOn) {
      this.micBoostOn = false;
      this.micBoostLabel = null;
      this.recorder.recorder.removeBoostChain();
      localStorage.removeItem('ssMicBoostOn');
      localStorage.removeItem('ssMicBoost');
      this.detectChanges();
      return;
    }
    if (!this.recorder?.recorder?.startAutoCalibration) return;
    this.micCalPhase    = 'listening';
    this.micCalProgress = 0;
    this.micCalLevel    = 0;
    this.detectChanges();
    this.recorder.recorder.startAutoCalibration(
      (progress: number, level: number) => {

        this.micCalProgress = progress;
        this.micCalLevel    = level;
        this.detectChanges();
      },
      (newBoost: number | null, detail: any) => {
        const isFirefox = /Firefox/.test(navigator.userAgent);
        const activeMicLabel = (this.sources?.audio?.find(
          (s: any) => s.deviceId === this.activeAudioDeviceId
        )?.label ?? '').toLowerCase();
        const isInternalMic = /built-?in|internal|laptop|macbook|imac/.test(activeMicLabel);
        const applyBoost = newBoost !== null ? newBoost : (isFirefox && isInternalMic ? 40 : 5);
        this.micCalPhase   = 'done';
        const dbLabel = this.gainToDb(applyBoost);
        this.micCalMessage = newBoost !== null
          ? `Boost set to ${dbLabel}`
          : `Weak signal — default boost ${dbLabel} applied`;
        this.micBoostOn    = true;
        this.micBoostLabel = dbLabel;
        localStorage.setItem('ssMicBoostOn', 'true');
        if (newBoost === null) {
          this.recorder.recorder.applyBoostChain(applyBoost).catch(() => {});
        }
        this.detectChanges();
        setTimeout(() => { this.micCalPhase = 'idle'; this.detectChanges(); }, 4000);
      },
      this.micCalTarget
    );
  }

  toggleMicBoost(): void {
    if (this.micBoostOn) {
      this.micBoostOn = false;
      this.micBoostLabel = null;
      this.recorder.recorder.removeBoostChain();
      localStorage.setItem('ssMicBoostOn', 'false');
    } else {
      this.micBoostOn = true;
      const saved = parseFloat(localStorage.getItem('ssMicBoost') || '0');
      const isFirefox = /Firefox/.test(navigator.userAgent);
      const boostValue = (saved > 0 && !isNaN(saved)) ? saved : (isFirefox ? 20 : 5);
      this.recorder.recorder.applyBoostChain(boostValue).catch(() => {
        this.micBoostOn = false;
        this.detectChanges();
      });
      localStorage.setItem('ssMicBoostOn', 'true');
    }
    this.detectChanges();
  }

  onSyncChange(e) {
    if (this.recorder.superPlayer.playing) {
      this.pause();
      this.UIState = 'reviewPaused';
      this.changeUIState();
    }
    this.sync = e;
    localStorage.setItem('sync', String(this.sync));
    this.recorder.sync(e);
  }

  // ── Sync nudge buttons (± beside the slider) ───────────────────────────────
  // Click a button = ±1 ms; press and hold = repeats by ±5 ms. Clamped -500..500.
  private syncHoldTimer: any = null;
  private syncHoldInterval: any = null;

  nudgeSync(delta: number): void {
    const next = Math.max(-500, Math.min(500, this.sync + delta));
    if (next !== this.sync) {
      this.onSyncChange(next);
      this.detectChanges();
    }
  }

  startSyncNudge(delta: number): void {
    this.nudgeSync(delta);            // immediate single step (±1)
    this.stopSyncNudge();
    const dir = delta < 0 ? -1 : 1;
    this.syncHoldTimer = setTimeout(() => {
      this.syncHoldInterval = setInterval(() => this.nudgeSync(5 * dir), 120);
    }, 400);
  }

  stopSyncNudge(): void {
    if (this.syncHoldTimer) { clearTimeout(this.syncHoldTimer); this.syncHoldTimer = null; }
    if (this.syncHoldInterval) { clearInterval(this.syncHoldInterval); this.syncHoldInterval = null; }
  }
  onSyncClose() {
    this.containerOpen = false;
    this.syncBar = false;
    this.detectChanges();
  }
  onVolumeClick() {
    this.containerOpen = true;
    this.volumeBar = true;
    this.detectChanges();
  }
  onMicVolumeChange(e) {
    let value = parseFloat(e);
    this.micVolume = value;
    this.recorder.changeVocalVolume(value);
  }
  onMusicVolumeChange(e) {
    let value = parseFloat(e);
    this.musicVolume = value;
    this.recorder.changeMusicVolume(value);
  }
  onVolumeClose() {
    this.containerOpen = false;
    this.volumeBar = false;
    this.detectChanges();
  }
  onLyricSpeedClick() {
    this.containerOpen = true;
    this.lyricBar = true;
    this.detectChanges();
  }
  onLyricSpeedChange(e) {
    let value = Math.round(e * 0.6 * 10) / 10; //rounds to one digit place
    if (this.recorder.superPlayer.playing) {
      this.pause();
      if (this.recorder.mode == 'review') {
        this.UIState = 'reviewPaused';
      } else if (this.UIState != 'default') {
        this.UIState = 'practicePaused';
      }
      this.changeUIState();
    }
    this.lyricSpeed = value / 0.6;
    this.recorder.lyricsPlayer.changeSpeed(value);
  }
  onLyricSpeedClose() {
    this.lyricBar = false;
    this.containerOpen = false;
    this.detectChanges();
  }

  onLyricSizeClick() {
    this.containerOpen = true;
    this.lyricSizeBar = true;
    this.detectChanges();
  }

  onLyricSizeChange(e) {
    this.lyricSize = Math.round(e);
    const scale = 1 + this.lyricSize * 0.15;
    document.documentElement.style.setProperty('--lyric-scale', String(scale));
    localStorage.setItem('ssLyricSize', String(this.lyricSize));
  }

  onContrastChange(v: number): void {
    this.contrast = v;
    this._applyContrast(v);
    localStorage.setItem('ssContrast', String(v));
  }

  /** Reset all Lighting controls to defaults: Brightness 0, Tone Natural, Contrast 50. */
  resetLighting(): void {
    this.recorderStore.setBrightness(0);
    this.recorderStore.setToneOn(false);
    this.recorderStore.setWarmth(50);
    this.onContrastChange(50);
    // Sun Mode: clear the toggle + the stashed pre-Sun snapshot, otherwise the
    // button lies about state (looks on while lighting is at 0) and a later
    // toggle-off would silently restore the stale values.
    this.sunMode = false;
    this._preSun = null;
    // Stage Mode: clear the chrome-dim toggle so the whole Lighting header row
    // returns to a clean neutral state. Persisted preference is cleared too.
    this.stageMode = false;
    localStorage.setItem('ssStageMode', 'false');
  }

  /**
   * Stage Mode — when enabled, all chrome (panels, header) dims
   * during recording / practicing / countdown so the user's eye lands on the
   * camera and lyrics instead of the controls. Pure user preference; doesn't
   * affect the footer where the Stop button lives.
   */
  stageMode = localStorage.getItem('ssStageMode') === 'true';

  /** Contrast collapse — least-tuned slider in Lighting hides behind a "more" link. */
  contrastExpanded = localStorage.getItem('ssContrastExpanded') === 'true';

  toggleContrastExpanded(): void {
    this.contrastExpanded = !this.contrastExpanded;
    localStorage.setItem('ssContrastExpanded', String(this.contrastExpanded));
  }

  toggleStageMode(): void {
    this.stageMode = !this.stageMode;
    localStorage.setItem('ssStageMode', String(this.stageMode));
  }

  @HostBinding('class.stage-mode-active') get stageModeActive(): boolean {
    return this.stageMode;
  }

  /**
   * Sun Mode — one-tap "max white fill light". Saves the current
   * brightness / toneOn / warmth on activation and restores them on
   * deactivation, so it behaves as a non-destructive toggle (opposite of
   * Stage Mode's dim toggle).
   */
  sunMode = false;
  private _preSun: { brightness: number; toneOn: boolean; warmth: number } | null = null;

  toggleSunMode(): void {
    this.sunMode = !this.sunMode;
    if (this.sunMode) {
      const fl = this.recorderStore.fillLight();
      this._preSun = { brightness: fl.brightness, toneOn: fl.toneOn, warmth: fl.warmth };
      this.recorderStore.setBrightness(100);
      this.recorderStore.setToneOn(true);
      this.recorderStore.setWarmth(50);
    } else if (this._preSun) {
      this.recorderStore.setBrightness(this._preSun.brightness);
      this.recorderStore.setToneOn(this._preSun.toneOn);
      this.recorderStore.setWarmth(this._preSun.warmth);
      this._preSun = null;
    }
  }

  /**
   * Called when the user moves the brightness slider directly. If Sun Mode
   * is active, the user is overriding it — clear the flag and drop the
   * remembered pre-Sun state (no restore on next toggle off).
   */
  onUserBrightnessChange(): void {
    if (this.sunMode) {
      this.sunMode = false;
      this._preSun = null;
    }
  }

  @HostBinding('class.contrast-active') get contrastActive(): boolean {
    return this.contrast !== 50;
  }

  /**
   * True when the active theme's shell bg luminance > 0.55 — used to keep
   * panel text dark across all brightness levels (the fill-light-active
   * dynamic text ramp is designed for dark themes lifting toward bright,
   * and produces low-contrast results on themes that are already bright).
   */
  @HostBinding('class.is-light-theme') get isLightTheme(): boolean {
    const shellBg = this.themeService.activeTheme.tokens['--shell-bg'] ?? '';
    const hex = shellBg.match(/#[0-9a-fA-F]{6}/)?.[0] ?? '#04071c';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55;
  }

  private _applyContrast(v: number): void {
    if (v === 50) {
      document.documentElement.style.removeProperty('--panel-brightness');
    } else {
      const brightness = 1 + (v - 50) / 50 * 0.35;
      document.documentElement.style.setProperty('--panel-brightness', brightness.toFixed(3));
    }
  }

  onLyricSizeClose() {
    this.lyricSizeBar = false;
    this.containerOpen = false;
    this.detectChanges();
  }

  onChangeMMSB() {
    if (this.mmsb) {
      this.recorder.superPlayer.mmsbOn();
    } else {
      this.recorder.superPlayer.mmsbOff();
    }
    //this.mmsb = !this.mmsb

    this.detectChanges();
  }
  upload() {
    if (this.uploaded) {
      return;
    }
    let song = this.jam ? this.song.song : this.song;
    let payload = {
      recording: this.recorder.recorder.blob,
      payload: {
        meta: {
          latency: this.sync,
          mic_volume: this.micVolume,
          base_volume: this.musicVolume,
          pitch: this.pitch.toString(),
        },
        part: this.part,
      },
      uuid: this.uuid,
      mmsb: this.mmsb,
      song: song,
    };
    if (payload.payload.meta.pitch == '0' || this.jam) {
      delete payload.payload.meta.pitch;
    } else {
      payload.payload.meta.pitch =
        (this.pitch > 0 ? 'RAISE_0' : 'LOWER_0') + Math.abs(this.pitch);
    }
    this.data.storage = payload;
    this.uploaded = true;
    gtag('event', 'sing', {
      action: 'Proceed To Save',
      songId: this.id.toString(),
    });
    // this.sendGtagFilters();
    this.router.navigate(['/d/sing-uploader']);
  }
  ngOnDestroy() {
    this.stopScrubber();
    this.stopSyncNudge();
    this.activeBursts = [];
    if (
      typeof this.recorder != 'undefined' &&
      typeof this.recorder != 'string'
    ) {
      this.pause();
      if (this.recorder?.recorder) {
        this.recorder.recorder.stopStream();
      }
      this.recorder = null;
    }
    if (this.fullscreen && !this.restarting) {
      this.fullscreen = false;
    }
    if (!this.uploaded || typeof this.recorder == 'string') {
      window.location.reload();
    }
  }
  onOtherSingers() {
    if (this.jam) {
      this.otherSingersElem.open(this.song.song.song.id);
    } else {
      this.otherSingersElem.open(this.id);
    }
  }
  toggleFullscreen() {
    if (this.fullscreen) {
      this.fullscreen = false;
      this.fsPanelLeftOpen = false;
      this.fsPanelRightOpen = false;
    } else {
      this.fullscreen = true;
      this.fsPanelLeftOpen = false;
      this.fsPanelRightOpen = false;
    }
    this.detectChanges();
  }

  toggleFsPanelLeft(): void {
    this.fsPanelLeftOpen = !this.fsPanelLeftOpen;
    this.detectChanges();
  }

  toggleFsPanelRight(): void {
    this.fsPanelRightOpen = !this.fsPanelRightOpen;
    this.detectChanges();
  }

  closeFsPanelLeft(): void {
    this.fsPanelLeftOpen = false;
    this.detectChanges();
  }

  closeFsPanelRight(): void {
    this.fsPanelRightOpen = false;
    this.detectChanges();
  }

  changeUIState() {
    if (this.UIState !== 'practicing' && this.UIState !== 'practicingPaused') {
      this.practiceElapsed = 0;
    }
    //behold, the grand switch statement!
    switch (this.UIState) {
      case 'default':
        this.UI.recordButton = true;
        this.UI.filterButton = true;
        this.UI.framesButton = true;
        this.UI.practiceButton = true;
        this.UI.restartButton = false;
        this.UI.reviewButton = false;
        this.UI.pitchButton = true;
        this.UI.volumeButton = true;
        this.UI.lyricButton = false;
        this.UI.lyricSizeButton = true;
        this.UI.syncButton = false;
        this.UI.mmsbButton = false;
        this.UI.cameraButton = true;
        this.UI.micButton = true;
        this.UI.saveOrTryAgainButtons = false;
        this.UI.pauseButton = false;
        this.UI.resumePracticeButton = false;
        this.UI.stopRecordingButton = false;
        break;
      case 'countdown':
        this.UI.recordButton = false;
        this.UI.filterButton = true;
        this.UI.framesButton = true;
        this.UI.practiceButton = false;
        this.UI.restartButton = false;
        this.UI.reviewButton = false;
        this.UI.pitchButton = false;
        this.UI.volumeButton = true;
        this.UI.lyricButton = false;
        this.UI.lyricSizeButton = false;
        this.UI.syncButton = false;
        this.UI.mmsbButton = false;
        this.UI.cameraButton = false;
        this.UI.micButton = false;
        this.UI.saveOrTryAgainButtons = false;
        this.UI.pauseButton = false;
        this.UI.resumePracticeButton = false;
        this.UI.stopRecordingButton = false;
        break;
      case 'noPermissions':
        this.UI.recordButton = false;
        this.UI.filterButton = false;
        this.UI.framesButton = false;
        this.UI.practiceButton = false;
        this.UI.restartButton = false;
        this.UI.reviewButton = false;
        this.UI.pitchButton = false;
        this.UI.volumeButton = false;
        this.UI.lyricButton = false;
        this.UI.lyricSizeButton = false;
        this.UI.syncButton = false;
        this.UI.mmsbButton = false;
        this.UI.cameraButton = false;
        this.UI.micButton = false;
        this.UI.saveOrTryAgainButtons = false;
        this.UI.pauseButton = false;
        this.UI.resumePracticeButton = false;
        this.UI.stopRecordingButton = false;
        break;
      case 'recording':
        this.UI.recordButton = false;
        this.UI.filterButton = true;
        this.UI.framesButton = true;
        this.UI.practiceButton = false;
        this.UI.restartButton = false;
        this.UI.reviewButton = false;
        this.UI.pitchButton = false;
        this.UI.volumeButton = true;
        this.UI.lyricButton = false;
        this.UI.lyricSizeButton = true;
        this.UI.syncButton = false;
        this.UI.mmsbButton = false;
        this.UI.cameraButton = false;
        this.UI.micButton = false;
        this.UI.saveOrTryAgainButtons = false;
        this.UI.pauseButton = false;
        this.UI.resumePracticeButton = false;
        this.UI.stopRecordingButton = true;
        this.step = 'Recording';
        break;
      case 'practicing':
        this.UI.recordButton = false;
        this.UI.filterButton = true;
        this.UI.framesButton = true;
        this.UI.practiceButton = false;
        this.UI.restartButton = false;
        this.UI.reviewButton = false;
        this.UI.pitchButton = true;
        this.UI.volumeButton = true;
        this.UI.lyricButton = false;
        this.UI.lyricSizeButton = true;
        this.UI.syncButton = false;
        this.UI.mmsbButton = false;
        this.UI.cameraButton = true;
        this.UI.micButton = true;
        this.UI.saveOrTryAgainButtons = false;
        this.UI.pauseButton = true;
        this.UI.resumePracticeButton = false;
        this.UI.stopRecordingButton = false;
        this.step = 'Practice Mode';
        break;
      case 'practicingPaused':
        this.UI.recordButton = true;
        this.UI.filterButton = true;
        this.UI.framesButton = true;
        this.UI.practiceButton = false;
        this.UI.restartButton = true;
        this.UI.reviewButton = false;
        this.UI.pitchButton = true;
        this.UI.volumeButton = true;
        this.UI.lyricButton = false;
        this.UI.lyricSizeButton = true;
        this.UI.syncButton = false;
        this.UI.mmsbButton = false;
        this.UI.cameraButton = true;
        this.UI.micButton = true;
        this.UI.saveOrTryAgainButtons = false;
        this.UI.pauseButton = false;
        this.UI.resumePracticeButton = true;
        this.UI.stopRecordingButton = false;
        this.step = 'Practice Mode';
        break;
      case 'review':
        this.filters = false;
        this.UI.recordButton = false;
        this.UI.filterButton = false;
        this.UI.framesButton = false;
        this.UI.practiceButton = false;
        this.UI.restartButton = false;
        this.UI.reviewButton = false;
        this.UI.pitchButton = false;
        this.UI.volumeButton = true;
        this.UI.lyricButton = false;
        this.UI.lyricSizeButton = true;
        this.UI.syncButton = true;
        this.UI.mmsbButton = true;
        this.UI.cameraButton = false;
        this.UI.micButton = false;
        this.UI.saveOrTryAgainButtons = true;
        this.UI.pauseButton = true;
        this.UI.resumePracticeButton = false;
        this.UI.stopRecordingButton = false;
        this.step = 'Review';
        break;
      case 'reviewPaused':
        this.UI.recordButton = false;
        this.UI.filterButton = false;
        this.UI.framesButton = false;
        this.UI.practiceButton = false;
        this.UI.restartButton = false;
        this.UI.reviewButton = false;
        this.UI.pitchButton = false;
        this.UI.volumeButton = true;
        this.UI.lyricButton = false;
        this.UI.lyricSizeButton = true;
        this.UI.syncButton = true;
        this.UI.mmsbButton = true;
        this.UI.cameraButton = false;
        this.UI.micButton = false;
        this.UI.saveOrTryAgainButtons = true;
        this.UI.pauseButton = false;
        this.UI.resumePracticeButton = true;
        this.UI.stopRecordingButton = false;
        this.step = 'Review';
        break;
    }
    // if(this.jam && (this.UIState == 'default' || this.UIState == 'recording' || this.UIState == 'countdown' || this.UIState == 'practicing' || this.UIState == 'practicingPaused')) {
    //   this.UI.volumeButton = false;
    // }

    if (!this.profileService.user.gold) {
      this.UI.filterButton = false;
      this.UI.framesButton = false;
      this.UI.pitchButton = false;
    }
    if (this.recorder.noCam) {
      this.UI.cameraButton = false;
    }
    // if(this.jam && this.loaded) {
    //  this.changeDuetStyles()
    //} else {
    this.detectChanges();
    // }
    this.syncRecorderStoreState();
  }

  private syncRecorderStoreState(): void {
    switch (this.UIState) {
      case 'recording':
      case 'countdown':
        if (this.recorderStore.recState() !== 'recording') {
          this.recorderStore.setRecState('recording');
        }
        break;
      case 'reviewPaused':
      case 'review':
        if (this.recorderStore.recState() !== 'review') {
          this.recorderStore.setRecState('review');
        }
        break;
      case 'practicing':
      case 'practicingPaused':
      case 'practicePaused':
        if (this.recorderStore.recState() !== 'practicing') {
          this.recorderStore.setRecState('practicing');
        }
        break;
      default:
        if (this.recorderStore.recState() !== 'idle') {
          this.recorderStore.setRecState('idle');
        }
        break;
    }
  }

  toAdmin(ref) {
    if (this.isControlPressed) {
      this.userService.getIsAdmin().subscribe(
        (response) => {
          if (response.success) {
            let url: string =
              environment.baseAPIUrl + '/admin/music/songs/' + ref + '/lyrics';
            window.open(url);
            this.isControlPressed = false;
          }
        },
        (error) => {}
      );
    }
    this.isControlPressed = false;
  }
  sendGtagFilters() {
    let filterName: string = this.filterName;
    let selectedFrameName: string = this.recorder.selectedFrame.name;
    if (!filterName.length) {
      filterName = 'none';
    }
    if (!selectedFrameName.length) {
      selectedFrameName = 'none';
    }
    gtag('event', 'filters', {
      filter: filterName,
      frame: selectedFrameName,
    });
  }

  sendGtagLoad(recID, gold, solo, err) {
    if (err === 'none') {
      gtag('event', 'sing', {
        action: 'Load Song',
        songId: recID.toString(),
        solo: solo,
        goldMember: gold.toString(),
      });
    } else {
      gtag('event', 'sing', {
        action: 'Load Song',
        songId: recID.toString(),
        solo: solo,
        error: err,
        goldMember: gold.toString(),
      });
    }
  }

    // ─────────────────────────────────────────────────────────────────────────────
  // Additions to superrecorder.component.ts
  // Drop these into the existing class — no new imports needed beyond what's there
  // ─────────────────────────────────────────────────────────────────────────────

  // ── State ─────────────────────────────────────────────────────────────────────

  // Add these alongside your existing micDropDown / videoDropDown booleans



  // ── On input_sources_ready ────────────────────────────────────────────────────
  // In your existing handler where you do `this.sources = event.audio` — add:

  onInputSourcesReady(sources: any): void {
    this.sources = { audio: sources.audio, video: sources.video };

    // The boost node replaces the original mic track with a MediaStreamDestination track
    // whose getSettings().deviceId is unreliable (empty string or random internal ID).
    // The recorder saves the real device IDs to localStorage before the track swap, so
    // read from there as the authoritative source.
    const audioDeviceId = localStorage.getItem('audioDevice') || null;
    const videoDeviceId = localStorage.getItem('videoDevice') || null;

    this.activeAudioDeviceId = audioDeviceId
      ?? this.activeAudioDeviceId
      ?? this.sources.audio?.[0]?.deviceId
      ?? null;

    this.activeVideoDeviceId = videoDeviceId
      ?? this.activeVideoDeviceId
      ?? this.sources.video?.[0]?.deviceId
      ?? null;

    // Let the sync modal reuse the recorder's mic stream — avoids a second permission prompt
    (window as any).__ssRecorderStream = this.recorder?.recorder?.stream ?? null;
    (window as any).__ssRecorderAudioDeviceId = this.activeAudioDeviceId ?? null;

    // Expose the recorder's AudioContext so the sync modal can suspend it during measurement.
    (window as any).__ssRecorderAudioCtx =
      this.recorder?.superPlayer?.webAudio?.audioContext ?? null;
  }


  // ── Active-device helpers ─────────────────────────────────────────────────────

  isActiveMic(deviceId: string): boolean {
    return this.activeAudioDeviceId === deviceId;
  }

  isActiveCam(deviceId: string): boolean {
    return this.activeVideoDeviceId === deviceId;
  }


  // ── Trigger label getters ─────────────────────────────────────────────────────
  // These drive the "MacBook Pro Microphone" text shown on the trigger button.

  get selectedMicLabel(): string {
    if (!this.sources?.audio?.length) return 'Microphone';
    const match = this.sources.audio.find(
      (s: any) => s.deviceId === this.activeAudioDeviceId
    );
    return match ? this.shortDeviceLabel(match.label) : 'Microphone';
  }

  get selectedCamLabel(): string {
    if (this.recorder?.recorder?.noCam) return 'Camera Off';
    if (!this.sources?.video?.length) return 'Camera';
    const match = this.sources.video.find(
      (s: any) => s.deviceId === this.activeVideoDeviceId
    );
    return match ? this.shortDeviceLabel(match.label) : 'Camera';
  }


  // ── Badge helper ──────────────────────────────────────────────────────────────
  // Returns true for known virtual audio drivers so we can show a "Virtual" badge

  isVirtual(label: string): boolean {
    if (!label) return false;
    const virtualKeywords = ['virtual', 'vb-cable', 'blackhole', 'manycam', 'loopback'];
    return virtualKeywords.some(kw => label.toLowerCase().includes(kw));
  }


  // ── Device change overrides ───────────────────────────────────────────────────
  // Wrap your existing changeMicSource / changeCamSource to update active ID




  // ── Close dropdowns on outside click ─────────────────────────────────────────
  // Add this to your existing @HostListener or document click handler




  // ── Private: shorten raw browser device labels ────────────────────────────────
  // Strips trailing "(0000:0001)" style IDs and "Default - " prefixes

  private moveMicMeter(): void {
    const canvas = document.getElementById('micMeter') as HTMLCanvasElement | null;
    const slot = document.getElementById('mic-meter-slot');
    if (!canvas || !slot || slot.contains(canvas)) return;
    // Rotate -90deg so the engine's bottom-to-top fill appears as left-to-right.
    // CSS height = slot width so the rotated canvas exactly fills the slot.
    const w = slot.clientWidth || 200;
    canvas.style.cssText = `display:block;position:absolute;width:6px;height:${w}px;transform:rotate(90deg);transform-origin:0 0;left:${w}px;top:0;border-radius:3px;`;
    slot.appendChild(canvas);
  }

  private shortDeviceLabel(label: string): string {
    if (!label) return 'Unknown device';
    return label
      .replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)/i, '') // strip hardware IDs
      .replace(/^Default\s*[-–]\s*/i, '')               // strip "Default - "
      .trim();
  }


}
